import {
  Compass,
  ClipboardCheck,
  Sparkles,
  FileBarChart,
  Store,
  Users2,
  Rocket,
  Building2,
  UserCog,
  Inbox,
  Mail,
  Briefcase,
  Settings2,
  BarChart3,
  Brain,
  FlaskConical,
  TrendingUp,
  FileText,
  ChevronDown,
  Settings as SettingsIcon,
  LifeBuoy,
  CalendarDays,
  UserCheck,
  Building,
  type LucideIcon,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { ModuleKey } from "@/lib/modules";
import { RoleBadges } from "@/components/RoleBadges";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { usePilotEnrollment } from "@/hooks/usePilotEnrollment";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  end?: boolean;
  module?: ModuleKey;
}

const ITEM_BASE = "sidebar-link";
const ITEM_ACTIVE = "sidebar-link-active";

function NavItemRow({ item }: { item: NavItem }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild>
        <NavLink
          to={item.url}
          end={item.end}
          className={ITEM_BASE}
          activeClassName={ITEM_ACTIVE}
        >
          <item.icon className="h-4 w-4 shrink-0 opacity-80" />
          <span>{item.title}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

const groupLabelCls =
  "text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80 px-3 pt-4 pb-1.5";

export function AppSidebar() {
  const {
    user,
    profile,
    roles,
    activeRole,
    setActiveRole,
    isModuleEnabled,
  } = useAuth();
  const { isPilotOnlyAssessor } = usePilotEnrollment();
  const isAdmin = activeRole === "admin";
  const isApprover = activeRole === "approver" || isAdmin;
  const isExpert = activeRole === "expert";
  const isVendor = activeRole === "vendor";

  const displayName = profile?.full_name || user?.email || "User";

  const assess: NavItem[] = [
    { title: "Age-Friendly Index", url: "/", icon: Compass, end: true },
    { title: "Assessments", url: "/assessments", icon: ClipboardCheck, module: "assessment" },
    ...(!isPilotOnlyAssessor ? [{ title: "AI Insights", url: "/ai-insights", icon: Sparkles, end: true } as NavItem] : []),
    { title: "Reports", url: "/assessments?view=reports", icon: FileBarChart },
    ...(isApprover ? [{ title: "Approval Queue", url: "/approvals", icon: ClipboardCheck } as NavItem] : []),
  ];

  const ecosystem: NavItem[] = ([
    { title: "Care and Wellbeing Marketplace", url: "/marketplace", icon: Store, module: "marketplace" as ModuleKey },
    { title: "Experts & Services", url: "/expert-advisory", icon: Users2, module: "experts" as ModuleKey },
    { title: "Testbed & Innovation Hub", url: "/testbed-hub", icon: FlaskConical },
    { title: "Organisations", url: "/organisations", icon: Building2 },
  ] as NavItem[]).filter((i) => !i.module || isModuleEnabled(i.module));

  const manage: NavItem[] = isAdmin
    ? [
        { title: "Users", url: "/users", icon: UserCog, end: true },
        { title: "Entities", url: "/entities", icon: Building2, end: true },
        { title: "Service Requests", url: "/admin/service-requests", icon: Inbox, end: true },
        { title: "Marketplace Leads", url: "/admin/marketplace-leads", icon: Mail, end: true },
        { title: "Vendor Management", url: "/admin/vendors", icon: Briefcase },
        { title: "System Configuration", url: "/framework", icon: Settings2 },
      ]
    : [];

  const intelligence: NavItem[] = isAdmin
    ? [
        { title: "LIP — Platform Home", url: "/lip", icon: Brain, end: true },
        { title: "Ontology & KG", url: "/lip/ontology", icon: Brain },
        { title: "Corpus & Repository", url: "/lip/corpus", icon: FileText },
        { title: "Longevity AI Layer", url: "/lip/ai", icon: FlaskConical },
        { title: "Applications & API", url: "/lip/applications", icon: Rocket },
        { title: "API Catalog", url: "/lip/api-catalog", icon: FileText },
        { title: "Roadmap", url: "/lip/roadmap", icon: TrendingUp },
        { title: "Analytics", url: "/ai-insights?tab=analytics", icon: BarChart3 },
      ]
    : [];

  const expertItems: NavItem[] = isExpert
    ? [
        { title: "My Expert Profile", url: "/expert-profile", icon: UserCheck, end: true },
        { title: "My Sessions", url: "/expert-sessions", icon: CalendarDays, end: true },
      ]
    : [];

  const vendorItems: NavItem[] = isVendor
    ? [
        { title: "Vendor Dashboard", url: "/vendor/dashboard", icon: Building, end: true },
        { title: "My Application", url: "/vendor/onboarding", icon: UserCheck, end: true },
      ]
    : [];

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      {/* Brand */}
      <div className="px-4 pt-5 pb-4">
        <Link to="/" className="block leading-tight">
          <span className="block text-base font-bold tracking-tight text-sidebar-foreground">
            SeniorHub
          </span>
          <span className="block text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground mt-0.5">
            Powered by SAFE Tracker AI
          </span>
        </Link>
      </div>

      <SidebarContent className="px-2">
        <Section label="Assess & Improve" items={assess} />
        {ecosystem.length > 0 && <Section label="Ecosystem" items={ecosystem} />}
        {manage.length > 0 && <Section label="Manage" items={manage} />}
        {intelligence.length > 0 && <Section label="Longevity Intelligence Platform" items={intelligence} />}
        {expertItems.length > 0 && <Section label="Expert" items={expertItems} />}
        {vendorItems.length > 0 && <Section label="Vendor" items={vendorItems} />}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-3 py-3">
        <div className="kpi-card p-3 bg-sidebar-accent/30">
          <p className="text-sm font-semibold truncate text-sidebar-foreground">
            {displayName}
          </p>
          {profile?.full_name && user?.email && (
            <p className="truncate text-[11px] text-muted-foreground mt-0.5">
              {user.email}
            </p>
          )}

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <RoleBadges roles={activeRole ? [activeRole] : roles.slice(0, 1)} size="sm" />
            {roles.length > 1 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    Switch
                    <ChevronDown className="ml-0.5 h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-40 p-1" sideOffset={6}>
                  {roles
                    .filter((r) => r !== "vendor")
                    .map((r) => (
                      <button
                        key={r}
                        onClick={() => setActiveRole(r)}
                        className={`w-full text-left rounded px-3 py-1.5 text-xs capitalize transition-colors ${
                          (activeRole ?? roles[0]) === r
                            ? "bg-accent font-medium text-accent-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                </PopoverContent>
              </Popover>
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <Link
              to="/settings"
              className="text-[11px] font-medium text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <SettingsIcon className="h-3 w-3" />
              Settings
            </Link>
            <Link
              to="/methodology"
              className="text-[11px] font-medium text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <LifeBuoy className="h-3 w-3" />
              Support
            </Link>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

function Section({ label, items }: { label: string; items: NavItem[] }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel className={groupLabelCls}>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu className="gap-0.5">
          {items.map((item) => (
            <NavItemRow key={item.title + item.url} item={item} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
